import { useMemo } from 'react';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { DeformableTransform } from '~/domain/dvf/deformableTransform';
import { DvfField } from '~/domain/dvf/dvfField';
import {
    DeformableRegistration,
    isDeformableRegistration,
} from '~/domain/patient/deformableRegistration';
import { ApprovableRegistration } from '~/domain/patient/approvableRegistration';
import { isRigidRegistration } from '~/domain/patient/rigidRegistration';
import {
    deformableFrameTransform,
    FrameTransform,
    rigidFrameTransform,
} from '~/domain/registration/frameTransform';
import {
    toDeformableTransform,
    toDvfField,
} from '~/hooks/workflows/dvf/deformableRegistrationField';
import { useRegistrationState } from '~/hooks/registration/useRegistrationStore';

export function useDeformationDescribingTheScreen():
    | DeformableRegistration
    | undefined {
    const registration = useRegistrationDescribingTheScreen();
    return registration && isDeformableRegistration(registration)
        ? registration
        : undefined;
}

export function useSelectedDeformationField(): DvfField | undefined {
    const deformable = useDeformationDescribingTheScreen();
    return useMemo(() => toDvfField(deformable), [deformable]);
}

export function useSelectedDeformableTransform():
    | DeformableTransform
    | undefined {
    const deformable = useDeformationDescribingTheScreen();
    return useMemo(() => toDeformableTransform(deformable), [deformable]);
}

export function useRegistrationDescribingTheScreen():
    | ApprovableRegistration
    | undefined {
    const patient = useSelectedPatient();
    const primaryImage = useSelectedPrimaryImage();
    const { selectedSopInstanceUid } = useRegistrationState();

    const registration = selectedSopInstanceUid
        ? patient?.getRegistrationWithSopInstanceUid(selectedSopInstanceUid)
        : undefined;
    return registration?.sourceFrameOfReferenceUid ===
        primaryImage?.frameOfReferenceUid
        ? registration
        : undefined;
}

export function useSelectedFrameTransform(): FrameTransform | undefined {
    const registration = useRegistrationDescribingTheScreen();
    const deformable = useSelectedDeformableTransform();
    return useMemo(() => {
        if (deformable) {
            return deformableFrameTransform(deformable);
        }
        if (registration && isRigidRegistration(registration)) {
            // The registration's own matrix maps moving onto fixed; this asks for fixed→moving.
            const fixedToMoving = registration.getTransformFromTo(
                registration.sourceFrameOfReferenceUid,
                registration.targetFrameOfReferenceUid
            );
            return fixedToMoving
                ? rigidFrameTransform(fixedToMoving)
                : undefined;
        }
        return undefined;
    }, [registration, deformable]);
}

export interface RegistrationFrames {
    readonly fixed: string;
    readonly moving: string;
}

export function useSelectedRegistrationFrames():
    | RegistrationFrames
    | undefined {
    const registration = useRegistrationDescribingTheScreen();
    return useMemo(
        () =>
            registration
                ? {
                      fixed: registration.sourceFrameOfReferenceUid,
                      moving: registration.targetFrameOfReferenceUid,
                  }
                : undefined,
        [registration]
    );
}
